import { ComponentInstance, computed, defineComponent, ref, Ref, useModel } from "vue";
import { now } from "../../skyward.base.time";
import { addScopedGlobalListener, DefaultVueProp, VueProp } from "../core/vueHelpers";
import Calendar, { CalendarExposed } from "./components/Calendar";
import FooterToolbar from "./components/FooterToolbar";
import MonthSelector from "./components/MonthSelector";

const DatePickerModalClasses =
{
    modal: "dateTimePickerModal",
    modalSecondary: "dateTimePickerModal--date",
    quickEELSelector: "quickEELSelector"
};

const DEFAULT_YEAR_LOW = 1900;

export default /*#__PURE__*/ defineComponent(
{
    name: "datePickerModal",
    emits:
    {
        "update:value": (_: Date) => true,
    },
    props: 
    {
        close: VueProp<() => void>(Function),
        inputRef: VueProp<Ref<HTMLInputElement | undefined, HTMLInputElement | undefined>>(Object),
        value: DefaultVueProp<Date>(Date, { default: () => now() }),
    },
    setup(props, context)
    {
        const value = useModel(props, "value");
        const today = now();
        const currentMonth = props.value ? props.value.getMonth() + 1 : today.getMonth() + 1;
        const currentYear = props.value ? props.value.getFullYear() : today.getFullYear();
        const month = ref<number>(currentMonth);
        const year = ref<number>(currentYear);
        const yearHigh = computed(() => props.value && props.value.getFullYear() > today.getFullYear() + 10 ? props.value.getFullYear() : today.getFullYear() + 10);
        const yearLow = computed(() => props.value && props.value.getFullYear() < DEFAULT_YEAR_LOW ? props.value.getFullYear() : DEFAULT_YEAR_LOW);
        const modal = ref<HTMLDivElement>();

        addScopedGlobalListener(document.body, "keydown", handleKeydown);
        const calendarRef = ref<ComponentInstance<typeof Calendar> & CalendarExposed>();

        context.expose(
        {
            focusCurrentDay() { calendarRef.value?.focusCurrentDay(); }
        });

        return () => (
            <div class={[DatePickerModalClasses.modal, DatePickerModalClasses.modalSecondary]} ref={modal}>
                <MonthSelector
                    month={month.value}
                    year={year.value}
                    yearLow={yearLow.value}
                    yearHigh={yearHigh.value}
                    onUpdate:month={value => (month.value = value)}
                    onUpdate:year={value => (year.value = value)}
                />
                <Calendar
                    month={month.value}
                    year={year.value}
                    yearLow={yearLow.value}
                    yearHigh={yearHigh.value}
                    selectedDate={value.value}
                    onUpdate:month={newMonth => month.value = newMonth}
                    onUpdate:year={newYear => year.value = newYear}
                    onUpdate:selectedDate={date => value.value = date}
                    ref={calendarRef}
                />
                <FooterToolbar buttonText="Today" onButtonClick={onTodayClick} onDoneClick={props.close} />
            </div>
        );

        function handleKeydown(event: JQuery.KeyDownEvent)
        {
            const key = event.key.toUpperCase();

            // Prevent hot keys in month and year search boxes
            if ((event.target as HTMLElement).getAttribute("class")?.includes("quickEELSelector"))
            {
                if (key !== "TAB")
                {
                    event.preventDefault();
                }
                return;
            }

            if (key === "ARROWUP" || key === "ARROWDOWN" || key === "PAGEUP" || key === "PAGEDOWN" || key === "HOME" || key === "END")
            {
                event.preventDefault();
            }

            if (event.altKey && (key === "ARROWUP" || key === "ARROWDOWN"))
            {
                props.inputRef.value?.focus();
                return;
            }

            // Handle shortcut keys (T = Today, R = Tomorrow, Y = Yesterday)
            if (key === "T" || key === "R" || key === "Y")
            {
                event.preventDefault();
                event.stopPropagation();
                const shortcutDate = now();
                if (key === "R")
                {
                    shortcutDate.setDate(shortcutDate.getDate() + 1);
                }
                if (key === "Y")
                {
                    shortcutDate.setDate(shortcutDate.getDate() - 1);
                }

                month.value = shortcutDate.getMonth() + 1;
                year.value = shortcutDate.getFullYear();
                value.value = shortcutDate;
            }
        }

        function onTodayClick()
        {
            const today = now();
            today.setHours(0, 0, 0, 0);
            month.value = today.getMonth() + 1;
            year.value = today.getFullYear();
            value.value = today;
            props.close();
        }
    }
});
